import { SkedEditor } from "../../sked-editor/sked-editor";
import { HashtagSearchPlugin } from "./sked-caption-plugins/hashtag-search-plugin";
import { HashtagPlugin } from "@lexical/react/LexicalHashtagPlugin";
import { TemplateSearchPlugin } from "./sked-caption-plugins/template-search-plugin";
import { LiMentionSearchPlugin } from "./sked-caption-plugins/li-mention-search-plugin";
import { FbMentionSearchPlugin } from "./sked-caption-plugins/fb-mention-search-plugin";
import { AtMentionSearchPlugin } from "./sked-caption-plugins/at-mention-search-plugin";
import { AtMentionPlugin } from "../../sked-editor/at-mention/at-mention-plugin";
import { TemplatesSelector } from "../components/templates-selector";
import { useSkedEditor } from "../../sked-editor/use-sked-editor";
import { EmojiPicker } from "../components/emoji-picker";

import { SuggestFunc } from "./types";
import { HistoryState } from "@lexical/react/LexicalHistoryPlugin";
import { EditorState } from "lexical";
import { DescriptionsList } from "../components/library-descriptions-picker";
import { useState } from "react";
import {
  $createSkedSocialMentionNode,
  SkedSocialMentionNode,
} from "./sked-social-mention";
import { ActionButton } from "./components/action-button";
import { TooltipWrapper } from "../../TooltipWrapper";
import { cn } from "../../../lib/utils";
import { regexConversionFactory } from "../../sked-editor/editor-state-conversion";
import { PostLinkedInAccountType } from "../../../shared/types/accounts";

interface SkedCaptionV2Props {
  initialValue?: string;
  autocomplete: PlatformServices;
  selectedPlatform: string;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  suggestions?: boolean;
  formId?: string;
  editorHistory?: SkedEditorHistory;
  descriptionsHidden?: boolean;
  descriptionsDisabled?: boolean;
  templateHidden?: boolean;
  counters?: {
    characters: number;
    charactersMax: number;
    hashtags?: number;
    hashtagsMax: number;
    mentions?: number;
    mentionsMax?: number;
  };
}

export interface SkedEditorHistory {
  historyState: HistoryState;
  lexicalState: EditorState | null;
  caption: string | null;
  handleEditorChange: (caption: string, state: EditorState) => void;
}

interface LookupService {
  suggest: SuggestFunc;
  remove?: (suggestion: { value: string; _id: string }) => Promise<void>;
}

interface PlatformServices {
  hashtag: LookupService;
  mention: LookupService;
  template: LookupService;
}
const theme = {
  hashtag: "text-blue-500 font-semibold",
  atMention:
    "group-[.at-mention-highlight]:text-blue-500 group-[.at-mention-highlight]:font-semibold",
};
export const SkedCaptionV2 = ({
  initialValue,
  autocomplete,
  selectedPlatform,
  selectedLinkedUrn,
  onChange,
  editorHistory,
  descriptionsHidden,
  formId,
  disabled,
  counters,
  descriptionsDisabled,
  hasMoreThanOnelinkedInAccount,
  selectedLinkedInAccountType,
  templateHidden,
}: SkedCaptionV2Props) => {
  const [skedEditor, handleEditorContext] = useSkedEditor();
  const [descriptionPickerState, setDescriptionPickerState] = useState(false);
  function resolveMentionPlugin() {
    if (selectedPlatform === "LI") {
      return (
        <LiMentionSearchPlugin
          lookupFunc={autocomplete.mention.suggest}
          liOrgUrn={selectedLinkedUrn}
          hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
          selectedLinkedInAccountType={selectedLinkedInAccountType}
        />
      );
    }
    if (selectedPlatform === "FB") {
      return (
        <FbMentionSearchPlugin lookupFunc={autocomplete.mention.suggest} />
      );
    }
    return (
      <AtMentionSearchPlugin
        lookupFunc={autocomplete.mention.suggest}
        onRemoveItem={async (item) => {
          await autocomplete.mention.remove?.({
            value: item.key,
            _id: item.key,
          });
        }}
      />
    );
  }
  const isAtMentionHighlighted =
    selectedPlatform !== "LI" && selectedPlatform !== "FB";

  const resolveInitInput = () => {
    if (editorHistory?.lexicalState && editorHistory.caption === initialValue) {
      return {
        type: "state",
        editorState: editorHistory.lexicalState,
      } as const;
    }
    return {
      type: "text",
      text: initialValue || "",
      textImportProcess: skedCaptionImport,
    } as const;
  };

  return (
    <div className="relative">
      <SkedEditor
        disabled={disabled}
        historyState={editorHistory?.historyState}
        onEditorContextReady={handleEditorContext}
        className={`font-(family-name:--font-inter) group ${isAtMentionHighlighted ? "at-mention-highlight" : ""} 
          min-h-[108px] px-[12px] pt-[8px] pb-[32px] outline-none border border-neutral-100 rounded-[8px]
          text-[14px] leading-[20px]
          `}
        theme={theme}
        initInput={resolveInitInput()}
        plugins={
          <>
            <HashtagPlugin />
            <AtMentionPlugin />
            <HashtagSearchPlugin
              lookupFunc={autocomplete.hashtag.suggest}
              onRemoveItem={async (item) => {
                await autocomplete.hashtag.remove?.({
                  value: item.key,
                  _id: item.key,
                });
              }}
            />
            {!templateHidden && (
              <TemplateSearchPlugin
                lookupFunc={autocomplete.template.suggest}
              />
            )}

            {resolveMentionPlugin()}
          </>
        }
        onCaptionChange={(caption, state) => {
          editorHistory?.handleEditorChange?.(caption, state);
          onChange?.(caption);
        }}
        initNodes={[SkedSocialMentionNode]}
      />
      <div
        className={cn("absolute left-[12px] bottom-[8px] mt-2 flex gap-[8px]", {
          hidden: disabled,
        })}
      >
        {!descriptionsHidden && (
          <TooltipWrapper
            tooltip={descriptionPickerState ? null : "Media descriptions"}
            placement={"top"}
          >
            <ActionButton
              disabled={descriptionsDisabled}
              onMouseDown={() =>
                setDescriptionPickerState(!descriptionPickerState)
              }
              focused={descriptionPickerState}
            >
              <i className="fa-regular fa-books  text-[12px]" />
            </ActionButton>
          </TooltipWrapper>
        )}
        {!templateHidden && (
          <TemplatesSelector
            onSelect={(text: string) => {
              skedEditor.insertText(text);
            }}
            newDesign
          />
        )}

        <EmojiPicker
          onSelect={(emoji) => skedEditor.insertText(emoji.native)}
          leftAlign
          newDesign
        />
      </div>
      {counters && (
        <div className="absolute right-[12px] bottom-[8px] flex gap-[8px] text-[12px] leading-[16px]">
          <CounterDisplay
            className="text-neutral-500"
            label="Characters:"
            value={counters.characters}
            max={counters.charactersMax}
          />
          {counters.hashtags !== undefined && (
            <CounterDisplay
              className="text-blue-500"
              label="Hashtags:"
              value={counters.hashtags}
              max={counters.hashtagsMax}
            />
          )}

          {counters.mentions !== undefined && (
            <CounterDisplay
              className="text-blue-500"
              label="Mentions:"
              value={counters.mentions}
              max={counters.mentionsMax}
            />
          )}
        </div>
      )}
      {descriptionPickerState && (
        <DescriptionsList
          onSelect={(text) => {
            skedEditor.insertText(text);
            setDescriptionPickerState(false);
          }}
          captionValue={initialValue || ""}
          formId={formId || ""}
        />
      )}
    </div>
  );
};

const CounterDisplay = ({
  value,
  max,
  label,
  className,
}: {
  max?: number;
  value: number;
  label: string;
  className?: string;
}) => {
  return (
    <div
      className={cn("font-(family-name:--font-inter)", className, {
        "text-red-500": max && value > max,
      })}
    >
      <span>{label} </span>
      <span>{value?.toLocaleString() || "0"}</span>
      {max ? <span>/{max?.toLocaleString() || "0"}</span> : undefined}
    </div>
  );
};

export const skedCaptionImport = regexConversionFactory({
  regex: /@\[([^)]+)\]\(([^\]]+)\)/g,
  func: (match) => {
    return $createSkedSocialMentionNode(
      `@[${match[1]}](${match[2]})`,
      match[2]
    );
  },
});
